import { useForm } from "react-hook-form";
import { RedeemVoucherRequest, redeemVoucherSchema } from "../../types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "src/components/Form/button";
import { Input } from "src/components/Form/input";
import { useRedeemVoucherMutation } from "../../credit-voucher.service";
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./sweetalert-custom.css";
import { FaArrowLeft } from "react-icons/fa";

export const RedeemVoucherForm = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [redeemVoucher, { isLoading }] = useRedeemVoucherMutation();
  const [redeemedData, setRedeemedData] = useState<{ amount: number; currency: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{ voucherCode: string }>({
    resolver: zodResolver(redeemVoucherSchema.omit({ userId: true })),
    defaultValues: {
      voucherCode: "",
    },
  });

  // Error message mappings
  const I18N_MESSAGES: Record<string, string> = {
    VOUCHER_NOT_FOUND: "Voucher not found",
    VOUCHER_ALREADY_REDEEMED: "This voucher has already been redeemed",
    VOUCHER_EXPIRED: "This voucher has expired",
    USER_NOT_FOUND: "User not found",
    VOUCHER_LANGUAGE_PRECONDITION_FAILED: "User space is not Dutch! and the project is not private mode",
    USER_DOES_NOT_HAVE_A_LEARNER_ROLE: "User does not have a learner role",
    USER_ALREADY_HAS_ACTIVE_SUBSCRIPTION: "User already has an active subscription",
    VOUCHER_FOR_TERMINATED_PROJECT: "Organization voucher for terminated project",
    VOUCHER_SUBSCRIPTION_END_DATE_EXPIRED: "Subscription end date has already expired",
    PREMIUM_VOUCHER_MISSING_END_DATE: "PREMIUM voucher must have subscriptionEndDate",
  };

  const ERROR_CODE_MESSAGES: Record<string, string> = {
    "permission-denied": "You can't process this function",
    "invalid-argument": "Voucher code is required",
    "internal": "Failed to determine subscription plan or end date for voucher",
  };

  const ERROR_MESSAGE_PATTERNS: Array<{ pattern: RegExp | string; message: string }> = [
    { pattern: /can't process/i, message: "You can't process this function" },
    { pattern: /voucher code is required|code is required/i, message: "Voucher code is required" },
    { pattern: /voucher not found/i, message: "Voucher not found" },
    { pattern: /user not found/i, message: "User not found" },
    { pattern: /already been redeemed|already redeemed/i, message: "This voucher has already been redeemed" },
    { pattern: /voucher has expired|expired.*voucher/i, message: "This voucher has expired" },
    { pattern: /not dutch|language/i, message: "User space is not Dutch! and the project is not private mode" },
    { pattern: /learner role/i, message: "User does not have a learner role" },
    { pattern: /active subscription|already has/i, message: "User already has an active subscription" },
    { pattern: /terminated project/i, message: "Organization voucher for terminated project" },
    { pattern: /subscription end date|end date/i, message: "Subscription end date has already expired" },
    { pattern: /premium voucher|subscriptionenddate/i, message: "PREMIUM voucher must have subscriptionEndDate" },
    { pattern: /failed to determine/i, message: "Failed to determine subscription plan or end date for voucher" },
  ];

  const extractErrorData = (error: unknown): { message: string; code: string; details: any } => {
    if (!error || typeof error !== 'object') {
      return { message: typeof error === 'string' ? error : '', code: '', details: null };
    }

    const err = error as Record<string, any>;
    
    // Check error.data (RTK Query structure)
    if (err.data) {
      const data = err.data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        // Handle nested data.data (RTK Query sometimes double-wraps)
        if (data.data && typeof data.data === 'object') {
          return {
            message: String(data.data.message || ''),
            code: String(data.data.code || ''),
            details: data.data.details,
          };
        }
        return {
          message: String(data.message || ''),
          code: String(data.code || ''),
          details: data.details,
        };
      }
      if (typeof data === 'string') {
        return { message: data, code: '', details: null };
      }
    }

    // Check error.error (nested error structure)
    if (err.error) {
      const nestedError = err.error;
      if (nestedError && typeof nestedError === 'object' && !Array.isArray(nestedError)) {
        if (nestedError.data && typeof nestedError.data === 'object') {
          return {
            message: String(nestedError.data.message || ''),
            code: String(nestedError.data.code || ''),
            details: nestedError.data.details,
          };
        }
        return {
          message: String(nestedError.message || ''),
          code: String(nestedError.code || ''),
          details: nestedError.details,
        };
      }
      if (typeof nestedError === 'string') {
        return { message: nestedError, code: '', details: null };
      }
    }

    // Check error directly
    if (err.message || err.code) {
      return {
        message: String(err.message || ''),
        code: String(err.code || ''),
        details: err.details,
      };
    }

    return { message: '', code: '', details: null };
  };

  const extractErrorMessage = (error: unknown): string => {
    const { message, code, details } = extractErrorData(error);

    // Priority 1: Check i18nKey in details (most specific)
    if (details?.i18nKey && I18N_MESSAGES[details.i18nKey]) {
      return I18N_MESSAGES[details.i18nKey];
    }

    // Priority 2: Check error code mappings
    if (code) {
      // Special handling for not-found code
      if (code === "not-found") {
        if (details?.i18nKey === "VOUCHER_NOT_FOUND") return "Voucher not found";
        if (details?.i18nKey === "USER_NOT_FOUND") return "User not found";
        if (message) {
          const lowerMsg = message.toLowerCase();
          if (lowerMsg.includes("voucher")) return "Voucher not found";
          if (lowerMsg.includes("user")) return "User not found";
        }
        return "Voucher not found";
      }

      // Special handling for failed-precondition code
      if (code === "failed-precondition") {
        if (details?.i18nKey && I18N_MESSAGES[details.i18nKey]) {
          return I18N_MESSAGES[details.i18nKey];
        }
        // Fallback to message pattern matching below
      }

      // Direct code mapping
      if (ERROR_CODE_MESSAGES[code]) {
        return ERROR_CODE_MESSAGES[code];
      }
    }

    // Priority 3: Check error message patterns
    if (message) {
      const lowerMsg = message.toLowerCase();
      for (const { pattern, message: mappedMessage } of ERROR_MESSAGE_PATTERNS) {
        if (typeof pattern === 'string') {
          if (lowerMsg.includes(pattern.toLowerCase())) {
            return mappedMessage;
          }
        } else {
          if (pattern.test(message)) {
            return mappedMessage;
          }
        }
      }
    }

    // Fallback to general message
    return "An unexpected error occurred. Please try again.";
  };

  const onSubmit = async (data: { voucherCode: string }) => {
    if (!userId) {
      Swal.fire({
        title: 'Error!',
        text: 'User ID is missing',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
      });
      return;
    }

    // Show loading state
    Swal.fire({
      title: 'Redeeming Voucher...',
      text: 'Please wait while we process your request',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    try {
      const redeemData: RedeemVoucherRequest = {
        voucherCode: data.voucherCode,
        userId: userId,
      };
      
      const result = await redeemVoucher(redeemData).unwrap();
      
      setRedeemedData({
        amount: result.amount,
        currency: result.currency,
      });

      // Show success message
      Swal.fire({
        title: 'Success!',
        html: `
          <div class="text-left">
            <p class="mb-2"><strong>Voucher Redeemed Successfully!</strong></p>
            <p class="mb-2"><strong>Amount:</strong> ${result.amount} ${result.currency}</p>
            <p class="mb-2"><strong>Voucher Code:</strong> ${data.voucherCode}</p>
            <p class="mb-2"><strong>User ID:</strong> ${userId}</p>
          </div>
        `,
        icon: 'success',
        confirmButtonColor: '#10b981',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'swal2-popup-custom',
          confirmButton: 'swal2-confirm-custom',
        }
      });
      
      reset();
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error);
      
      Swal.fire({
        title: 'Error!',
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'Try Again',
        customClass: {
          popup: 'swal2-popup-custom',
          confirmButton: 'swal2-confirm-custom',
        }
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/users/${userId}`)}
            className="group flex items-center px-4 py-2.5 bg-white hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 text-indigo-600 hover:text-indigo-700 font-medium rounded-lg border-2 border-indigo-200 hover:border-indigo-300 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
          >
            <FaArrowLeft className="mr-2 transition-transform duration-200 group-hover:-translate-x-1" />
            <span>Back to User Details</span>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <div className="bg-gradient-to-br from-white via-indigo-50 to-purple-50 rounded-2xl shadow-2xl p-8 border-2 border-indigo-100">
            <div className="mb-6">
              <div className="flex items-center mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl mr-3 flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Redeem Voucher</h3>
              </div>
              <p className="text-gray-600 text-sm">Enter the voucher code to redeem</p>
            </div>
            
            <div className="space-y-6">
              <div className="space-y-1">
                <Input 
                  label="Voucher Code"
                  registration={register("voucherCode")} 
                  error={errors.voucherCode?.message}
                  placeholder="Enter voucher code"
                  className="w-full"
                />
              </div>
              
              {userId && (
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">User ID</label>
                  <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-lg text-gray-700 font-medium shadow-sm">
                    {userId}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-center space-x-4 pt-6">
            <Button 
              type="submit" 
              disabled={isLoading}
              className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Redeeming...
                </div>
              ) : (
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Redeem Voucher
                </div>
              )}
            </Button>
          </div>
        </form>

        {/* Success Display */}
        {redeemedData && (
          <div className="mt-8 bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl mr-3 flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-emerald-800">Last Redeemed Voucher</h3>
            </div>
            <div className="space-y-2 bg-white/60 rounded-lg p-4 border border-emerald-100">
              <p className="text-gray-800 font-medium">
                <span className="text-emerald-700 font-semibold">Amount:</span>{" "}
                <span className="text-lg font-bold text-emerald-600">{redeemedData.amount} {redeemedData.currency}</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


